import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button,
    Avatar
} from '@mui/material';

import {
    Home as HomeIcon,
    Group as GroupIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    EmojiPeople as EmojiPeopleIcon,
    SentimentSatisfiedAlt as SentimentSatisfiedAltIcon,
    SentimentVeryDissatisfied as SentimentVeryDissatisfiedIcon,
    FormatListNumbered as FormatListNumberedIcon,
    Star as StarIcon,
    Person as PersonIcon,
    ArrowForwardIos as ArrowForwardIosIcon
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { id: 'introduction', label: 'ルームシェアの魅力とは？' },
    { id: 'basics', label: 'ルームシェアの基本と種類' },
    { id: 'merits', label: 'ルームシェアのメリット' },
    { id: 'demerits', label: 'ルームシェアのデメリット' },
    { id: 'interview', label: '経験者インタビュー＆リアルな声' },
    { id: 'suitability', label: '向いている人・向いていない人' },
    { id: 'advice', label: 'ルームシェアを成功させるコツ' }
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 1, sm: 3 }, pb: 6 }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem
                                    key={item.id}
                                    sx={{
                                        p: 0.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        minHeight: 36
                                    }}
                                >
                                    <Box sx={{
                                        minWidth: 32,
                                        minHeight: 32,
                                        bgcolor: '#f5f5f5',
                                        color: '#616161',
                                        fontWeight: 'bold',
                                        fontSize: '1.05rem',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 1
                                    }}>
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{
                                            cursor: 'pointer',
                                            fontSize: '0.97rem',
                                            pl: 0,
                                            py: 0.3,
                                            textAlign: 'left'
                                        }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入部分 */}
            <Box id="introduction" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアの魅力とは？
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    近年、都心部を中心に人気が高まる「ルームシェア」。<b>家賃を抑えながら、広い住空間や新しい人間関係を楽しめる</b>として、学生や社会人・クリエイター・外国人など幅広い層が選択肢の一つとしています。
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    一方で、「プライバシーは？トラブルは？」「実際の暮らしはどんな感じ？」といった不安や疑問も多いはず。本記事では、<b>経験者のリアルな声・専門家の視点</b>を交えて、ルームシェアのメリット・デメリットから、成功のコツや向いている人の特徴までを徹底解説します！
                </Typography>
                <Box sx={{ my: 3, width: '100%' }}>
                    <img
                        src="/images/blog/share-living-food_roomshare-merit-demerit-guide.jpg"
                        alt="明るい共有リビングで、男女3人のルームメイトたちが手作りの食事を囲みながら和やかに会話しているシーン。木目のテーブル、観葉植物、温かみのあるライトが居心地の良さを演出している"
                        style={{ width: '100%', borderRadius: 8, boxShadow: '0 2px 8px #e3f2fd' }}
                    />
                </Box>
                <Typography variant="body2" color="text.secondary">
                    この記事を読めば、ルームシェアの全体像と、自分に合うかの判断材料が必ず見つかります。
                </Typography>
            </Box>

            {/* ルームシェアの基本 */}
            <Box id="basics" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアの基本と種類
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={7}>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <b>ルームシェア</b>とは、複数人で1つの住居を共同で借りて暮らす住まい方のこと。家賃や光熱費、ネットなどのコストを分担しながら、<b>リビングやキッチンなどの共有スペース</b>を使い、<b>個室や寝室は個々に確保</b>するのが一般的です。
                        </Typography>
                        <Typography variant="body1">
                            近年は「シェアハウス」と呼ばれる専用物件や、友人同士で賃貸するパターン、マッチングサービスを介して見知らぬ人と住むケースなど多様化。住人の属性（学生・社会人・外国人など）や、趣味・価値観で集まるコンセプト型も人気です。
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={5} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Box sx={{ width: '100%' }}>
                            <img
                                src="/images/blog/share-house-types_roomshare-merit-demerit-guide.jpg"
                                alt="モダンなシェアハウスの外観と、カラフルな玄関ドアが並ぶ様子。複数人が出入りしやすいゆったりとしたエントランスと、開放感のある共用スペースが写っている"
                                style={{ width: '100%', borderRadius: 8, boxShadow: '0 2px 8px #e3f2fd' }}
                            />
                        </Box>
                    </Grid>
                </Grid>
                <List sx={{ mb: 2 }}>
                    <ListItem>
                        <ListItemIcon><HomeIcon color="primary" /></ListItemIcon>
                        <ListItemText
                            primary="友人・知人同士のルームシェア"
                            secondary="気心の知れたメンバーで始めやすい。生活リズムやルールも柔軟に決めやすい"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><GroupIcon color="primary" /></ListItemIcon>
                        <ListItemText
                            primary="マッチング型（他人同士・SNSやサービス経由）"
                            secondary="新しい出会いや刺激を求める人向き。事前の相性チェックやルール設定が重要"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><StarIcon color="primary" /></ListItemIcon>
                        <ListItemText
                            primary="コンセプト型シェアハウス"
                            secondary="趣味・職業・国際交流などテーマで住人が集まる。コミュニティ性が高い"
                        />
                    </ListItem>
                </List>
            </Box>

            {/* メリット */}
            <Box id="merits" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアのメリット
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアには<b>金銭面のメリット</b>だけでなく、<b>生活の質や人間関係</b>にプラスの効果をもたらすことも。主なメリットをピックアップしました。
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                                代表的なメリット一覧
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="家賃・光熱費の大幅節約" secondary="都心や好立地でもコストダウンが可能" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="広い部屋・キッチン・バスなど高グレード物件に住める" secondary="一人暮らしでは叶いにくい環境も実現" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="孤独感が減り、生活が楽しくなる" secondary="気軽な会話や食事で心もリフレッシュ" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="生活スキルや価値観が広がる" secondary="多様なバックグラウンドに触れ成長できる" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="家具・家電など初期費用の分担" secondary="引っ越しの負担も軽減" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={0} sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 2, height: '100%' }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <SentimentSatisfiedAltIcon color="primary" sx={{ mr: 1 }} />
                                体験者の声
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                「誰かと一緒にご飯を食べると、1日の疲れも吹き飛びます。趣味の話や仕事の悩みも気軽に相談できるのが大きな魅力！」（20代 社会人女性）
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                「学生時代からルームシェアしていますが、金銭的にも精神的にも助けられてきました。人と暮らすことで自分も成長できたと感じます！」（30代 男性）
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
                <Box sx={{ my: 2 }}>
                    <img
                        src="/images/blog/share-living-cleaning_roomshare-merit-demerit-guide.jpg"
                        alt="明るいダイニングキッチンで、3人のルームメイトが協力して掃除や片付けをしている場面。チームワークや共同生活の良さが伝わる和やかな雰囲気"
                        style={{ width: '100%', borderRadius: 8, boxShadow: '0 2px 8px #e3f2fd' }}
                    />
                </Box>
                <Typography variant="caption" color="text.secondary">
                    共同作業や日々の交流が、生活をより充実させてくれます。
                </Typography>
            </Box>

            {/* デメリット */}
            <Box id="demerits" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアのデメリット
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    一方で、ルームシェアには<b>気をつけたい注意点やストレス要因</b>も存在します。事前に知っておくことでトラブル予防につながります。
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <CancelIcon color="error" sx={{ mr: 1 }} />
                                主なデメリット一覧
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemIcon><CancelIcon color="error" /></ListItemIcon>
                                    <ListItemText primary="プライバシーや自由度が下がる" secondary="個室があっても完全な一人空間ではない" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CancelIcon color="error" /></ListItemIcon>
                                    <ListItemText primary="生活リズム・価値観の違いによるストレス" secondary="音・清潔感・ルール意識のズレがトラブルの種に" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CancelIcon color="error" /></ListItemIcon>
                                    <ListItemText primary="金銭トラブルのリスク" secondary="家賃・光熱費の未払い、負担割合の不満など" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CancelIcon color="error" /></ListItemIcon>
                                    <ListItemText primary="プライベートな来客や恋人問題" secondary="同居人への配慮やルール決めが必要" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CancelIcon color="error" /></ListItemIcon>
                                    <ListItemText primary="契約・退去時の手続きが煩雑" secondary="誰かが抜ける場合の家賃や契約変更手続き" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={0} sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 2, height: '100%' }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <SentimentVeryDissatisfiedIcon color="error" sx={{ mr: 1 }} />
                                体験者の声
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                「夜型の人と朝型の人で生活リズムが合わず、物音や明かりでストレスを感じたことがあります…」（20代 男性）
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                「掃除のやり方やゴミ出しルールの違いで何度か喧嘩になりました。最初にルールを決めれば良かったと後悔」（30代 女性）
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
                <Typography variant="caption" color="text.secondary">
                    こうしたデメリットを理解し、事前に対策することが快適なルームシェアの秘訣です。
                </Typography>
            </Box>

            {/* 経験者インタビュー */}
            <Box id="interview" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    経験者インタビュー＆リアルな声
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ maxWidth: 345, mb: 2, mx: 'auto' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Avatar src="/images/avatars/interview01.jpg" sx={{ mr: 2 }} />
                                    <Typography variant="subtitle1" fontWeight="bold">Aさん（28歳・ITエンジニア）</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <b>Q: ルームシェアを始めたきっかけは？</b><br />
                                    「社会人になって上京した際、家賃を抑えつつ都心に住みたかったからです。最初は知人2人で始めました。」
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <b>Q: 実際暮らしてみて？</b><br />
                                    「生活費が安く済むだけでなく、仕事の悩みも気軽にシェアできるのが助かっています。たまに朝までゲーム大会も(笑)」
                                </Typography>
                                <Typography variant="body2">
                                    <b>Q: 困ったことは？</b><br />
                                    「掃除やゴミ出しの分担で揉めたことがあったので、最初にルール化したのが良かったです。」
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ maxWidth: 345, mb: 2, mx: 'auto' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Avatar src="/images/avatars/interview02.jpg" sx={{ mr: 2 }} />
                                    <Typography variant="subtitle1" fontWeight="bold">Bさん（24歳・大学院生）</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <b>Q: どんなルームシェア？</b><br />
                                    「外国人留学生とのルームシェアです。文化の違いがあり、よくも悪くも毎日刺激的です。」
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <b>Q: 良かった点は？</b><br />
                                    「多様な価値観に触れて視野が広がりました。ルームメイトの友人を含めてイベントを企画したり、料理をシェアするのが楽しいです。」
                                </Typography>
                                <Typography variant="body2">
                                    <b>Q: 気をつけていることは？</b><br />
                                    「音や生活リズムの違いは思ったより大きいので、遠慮せず相談できる雰囲気が大事だと思います。」
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <Box sx={{ my: 3 }}>

                </Box>
                <Typography variant="body2" color="text.secondary">
                    ルームシェアならではの出会いや成長の機会が、人生を豊かにしてくれます。
                </Typography>
            </Box>

            {/* 向いている人・向いていない人 */}
            <Box id="suitability" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    向いている人・向いていない人
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2, mb: 2, height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <EmojiPeopleIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="subtitle1" fontWeight="bold">ルームシェアが向いている人</Typography>
                            </Box>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="人と協調して暮らすのが好き" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="コミュニケーションを楽しめる" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="生活費を抑えたい・コスパ重視" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="新しい出会いや経験を求める" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="柔軟にルール作り・調整できる" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2, mb: 2, height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <PersonIcon color="error" sx={{ mr: 1 }} />
                                <Typography variant="subtitle1" fontWeight="bold">ルームシェアが向いていない人</Typography>
                            </Box>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CancelIcon color="error" /></ListItemIcon>
                                    <ListItemText primary="一人の空間・静寂が絶対に必要" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CancelIcon color="error" /></ListItemIcon>
                                    <ListItemText primary="生活リズムや価値観を変えたくない" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CancelIcon color="error" /></ListItemIcon>
                                    <ListItemText primary="他人の細かい行動が気になる" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CancelIcon color="error" /></ListItemIcon>
                                    <ListItemText primary="ルール作りや話し合いが苦手" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CancelIcon color="error" /></ListItemIcon>
                                    <ListItemText primary="金銭トラブルに敏感・慎重すぎる" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    自分の性格やライフスタイルを振り返り、無理なく続けられるかを検討しましょう。
                </Typography>
            </Box>

            {/* 専門家アドバイス */}
            <Box id="advice" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアを成功させるコツ
                </Typography>
                <Divider sx={{ my: 2 }} />
                <List>
                    <ListItem>
                        <ListItemIcon><ArrowForwardIosIcon color="primary" /></ListItemIcon>
                        <ListItemText
                            primary="最初にルールや役割分担をしっかり決める"
                            secondary="掃除・ゴミ出し・支払いなど、曖昧にせず文書化すると安心"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><ArrowForwardIosIcon color="primary" /></ListItemIcon>
                        <ListItemText
                            primary="定期的に話し合い・ミーティングの場を設ける"
                            secondary="小さな不満も溜め込まず、オープンに相談"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><ArrowForwardIosIcon color="primary" /></ListItemIcon>
                        <ListItemText
                            primary="個室や自分の時間も大切にする"
                            secondary="無理に全てを共有せず、メリハリを意識"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><ArrowForwardIosIcon color="primary" /></ListItemIcon>
                        <ListItemText
                            primary="信頼できる人・相性の良い相手を選ぶ"
                            secondary="価値観や生活リズムのすり合わせが重要"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><ArrowForwardIosIcon color="primary" /></ListItemIcon>
                        <ListItemText
                            primary="家賃・契約などお金のことは明確に"
                            secondary="口約束ではなく書面やアプリで管理"
                        />
                    </ListItem>
                </List>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    ルームシェアは、事前の準備とコミュニケーションが成功へのカギ。最初の一歩を踏み出す勇気と、柔軟な姿勢を忘れずに！
                </Typography>
            </Box>

        </Box>
    );
};

const post = {
    id: 'roomshare-merit-demerit-guide',
    slug: 'roomshare-merit-demerit-guide',
    title: 'ルームシェアって実際どうなの？経験者が語るメリット・デメリット完全ガイド',
    excerpt: 'ルームシェアの基本や種類、メリット・デメリット、経験者インタビュー、向いている人・向いていない人までを徹底解説。ルームシェアを検討する人必見の完全ガイド。',
    coverImage: '/images/blog/cover-roomshare_roomshare-merit-demerit-guide.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。木目のテーブルや観葉植物、リラックスした雰囲気が印象的で、シェア生活の魅力や温かみが伝わる',
    publishedAt: '2024-06-13T00:00:00+09:00',
    isPublished: true,
    categories: ['住まい', 'ライフスタイル', 'ルームシェア'],
    tags: ['ルームシェア', 'シェアハウス', '共同生活'],
    author: {
        name: '松本 遼平',
        avatar: '/images/avatars/expert01.jpg',
        bio: '住宅・暮らしコンサルタント。10年以上のシェアハウス調査・居住経験を活かし、住まい選びや共同生活のアドバイスを発信中。',
        profilePicture: '/images/avatars/expert01.jpg'
    },
    viewCount: 1534,
    contentComponent: PostContent
};

export default post;